'use client'

import appStoreLogoEn from '@public/images/app_store.svg'
import appStoreLogoFr from '@public/images/app_store_fr.svg'
import googlePlayLogoEn from '@public/images/google_play.svg'
import googlePlayLogoFr from '@public/images/google_play_fr.svg'
import Image from 'next/image'
import React from 'react'

import { Button } from '@src/components/Button/Button'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useUserAgent } from '@src/hooks/useUserAgent'
import { useLocale } from '@src/i18n/LocaleProvider'
import { useTrackSelectCta } from '@src/utils/analytics.util'
import { cn } from '@src/utils/tailwind'

interface Props {
  className?: string
  title: string
  titleClassName?: string
  description: string
  descriptionClassName?: string
  defaultLink: string
  iosLink: string
  androidLink: string
  buttonText: string
  buttonClassName?: string
}

export function AppLink({
  className,
  defaultLink,
  iosLink,
  androidLink,
  title,
  titleClassName,
  description,
  descriptionClassName,
  buttonText,
  buttonClassName,
}: Props) {
  const { locale } = useLocale()
  const trackSelectCta = useTrackSelectCta()
  const userAgent = useUserAgent()

  const osName = userAgent?.os?.name
  const isIOS = osName === 'iOS'
  const isAndroid = osName === 'Android'
  const appStoreLogo: Record<string, any> = { en: appStoreLogoEn, fr: appStoreLogoFr }
  const googlePlayLogo: Record<string, any> = { en: googlePlayLogoEn, fr: googlePlayLogoFr }

  let linkHref = defaultLink
  if (isIOS) {
    linkHref = iosLink
  } else if (isAndroid) {
    linkHref = androidLink
  }

  return (
    <div className={cn(['text-center'], className)}>
      <div className={cn('text-heading-display-3 md:text-heading-3 font-medium', titleClassName)}>
        {title}
      </div>
      <div className={cn('mx-auto mt-1 w-[313px] text-body-sm md:w-auto', descriptionClassName)}>
        {description}
      </div>
      <LocalizedLink href={linkHref}>
        {isIOS && (
          <Image
            className="mx-auto mt-3.5 w-[202px]"
            src={appStoreLogo[locale.lang]}
            alt="app-store-logo"
          />
        )}
        {isAndroid && (
          <Image
            className="mx-auto mt-3.5 w-[202px]"
            src={googlePlayLogo[locale.lang]}
            alt="google-play-logo"
          />
        )}
        {!isIOS && !isAndroid && (
          <Button
            data-testid="applink-button"
            size="medium"
            className={cn('mt-6 max-w-[169px]', buttonClassName)}
            onClick={() => {
              trackSelectCta({ 'cta url': linkHref })
            }}
          >
            {buttonText?.toUpperCase()}
          </Button>
        )}
      </LocalizedLink>
    </div>
  )
}
